/* HEMDRAG FX v1 (Sep 26 2026), quality pass v1.1 (Sep 30 2026). Vanilla, no dependencies,
   loaded with defer.
   Parts: wordmark glint, scroll reveal, parallax, hero pause, card sheen, free shipping gauge,
   and (v1.1) crisp image sizes, the cart drawer scrim, the phone header logo hand-off, no
   breaks inside "Wide-Leg", and table scroll hints. v1.1 removed the card tilt, the card drips
   and the drip cursor.
   Every moving part checks prefers-reduced-motion. Nothing here changes layout: only classes,
   CSS variables, transforms and img sizes attributes. */
(() => {
  'use strict';
  if (window.__hdFx) return;
  window.__hdFx = true;

  const d = document;
  const root = d.documentElement;
  const cfgEl = d.getElementById('hd-fx-js');
  const cfg = (cfgEl && cfgEl.dataset) || {};
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const still = () => reduce.matches;
  const $$ = (sel, ctx = d) => Array.from(ctx.querySelectorAll(sel));

  root.classList.add('hd-fx');

  /* 1. Wordmark glint: mask a light sweep with the logo image itself. */
  function markGlint() {
    const mark = d.querySelector('.hd-hero__mark');
    const img = mark && mark.querySelector('img');
    if (!img || still()) return;
    const set = () => {
      const src = img.currentSrc || img.src;
      if (!src || !img.naturalWidth) return;
      const m = mark.getBoundingClientRect();
      const r = img.getBoundingClientRect();
      mark.style.setProperty('--hd-mark-mask', `url("${src}")`);
      mark.style.setProperty('--hd-mk-x', `${(r.left - m.left).toFixed(1)}px`);
      mark.style.setProperty('--hd-mk-y', `${(r.top - m.top).toFixed(1)}px`);
      mark.style.setProperty('--hd-mk-w', `${r.width.toFixed(1)}px`);
      mark.style.setProperty('--hd-mk-h', `${r.height.toFixed(1)}px`);
      mark.classList.add('hd-mark-ready');
    };
    if (img.complete) set(); else img.addEventListener('load', set, { once: true });
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(img);
  }

  /* 2. Scroll reveal. Only things still below the fold get a start state, so nothing the
        visitor can already see ever blinks. */
  let io = null;
  function reveal(el, kind) {
    if (!el || el.dataset.hdRv) return;
    el.dataset.hdRv = '1';
    if (still() || !('IntersectionObserver' in window)) return;
    if (el.getBoundingClientRect().top < innerHeight * 0.9) return;
    el.classList.add('hd-rv');
    if (kind) el.classList.add(`hd-rv--${kind}`);
    if (!io) {
      io = new IntersectionObserver((entries) => {
        let n = 0;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const t = e.target;
          t.style.setProperty('--hd-rv-d', `${Math.min(n++, 5) * 80}ms`);
          t.classList.add('hd-in');
          io.unobserve(t);
          if (t.classList.contains('hd-rv--card')) setTimeout(() => t.classList.add('hd-done'), 1600);
        }
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.12 });
    }
    io.observe(el);
  }
  function armReveals() {
    $$('.section-resource-list__header, .hd-pieces, .hd-look__intro, .hd-look__frame, .hd-marquee__band').forEach((el) => reveal(el));
    $$('.hd-mto, .section[data-testid^="divider-"]').forEach((el) => reveal(el, 'drip'));
    $$('.product-grid__item product-card, .resource-list__item product-card, product-recommendations product-card').forEach((el) => reveal(el, 'card'));
  }

  /* 3. Parallax: one passive scroll listener, one frame of work per scroll. */
  const par = { hero: null, flood: null, media: null, looks: [] };
  let ticking = false;
  // Horizon 4.x scrolls inside .page-wrapper, not the window; follow whichever one moves.
  const scrollerY = () => {
    const w = d.querySelector('.page-wrapper');
    if (w && w.scrollHeight > w.clientHeight + 1) return w.scrollTop;
    return window.scrollY || d.documentElement.scrollTop || 0;
  };
  function parallaxFrame() {
    ticking = false;
    const y = scrollerY();
    const vh = innerHeight;
    if (par.flood && y < vh * 1.5) {
      par.flood.style.setProperty('--hd-flood-y', `${(-Math.min(y, 420) * 0.1).toFixed(1)}px`);
    }
    if (par.media && y < vh * 1.5) {
      par.media.style.setProperty('--hd-media-y', `${(Math.min(y, 420) * 0.06).toFixed(1)}px`);
    }
    for (const img of par.looks) {
      const r = img.parentElement.getBoundingClientRect();
      if (r.bottom < -50 || r.top > vh + 50) continue;
      const off = (r.top + r.height / 2 - vh / 2) / vh;       // -1 .. 1 across the screen
      img.style.setProperty('--hd-par-y', `${(Math.max(-1, Math.min(1, off)) * -18).toFixed(1)}px`);
    }
  }
  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(parallaxFrame); } }
  function armParallax() {
    if (still()) return;
    par.flood = d.querySelector('.hd-hero__flood');
    par.media = d.querySelector('.hd-hero__media');
    par.looks = $$('.hd-look__img');
    if (!par.flood && !par.media && !par.looks.length) return;
    d.addEventListener('scroll', onScroll, { passive: true, capture: true });   // capture: catches inner scrollers too
    addEventListener('resize', onScroll, { passive: true });
    parallaxFrame();
  }

  /* 4. Pause the looping hero and marquee when they are off screen (saves battery). */
  function armPause() {
    if (!('IntersectionObserver' in window)) return;
    const po = new IntersectionObserver((entries) => {
      for (const e of entries) e.target.classList.toggle('hd-paused', !e.isIntersecting);
    });
    $$('.hd-hero__flood, .hd-marquee').forEach((el) => po.observe(el));
    const mq = new IntersectionObserver((entries) => {
      for (const e of entries) {
        $$('.hd-marquee__track', e.target).forEach((t) => { t.style.animationPlayState = e.isIntersecting ? '' : 'paused'; });
      }
    });
    $$('.hd-marquee').forEach((el) => mq.observe(el));
  }

  /* 5. Cards: a sheen that follows the pointer and a "View piece" chip (mouse only). */
  function enhanceCard(card) {
    if (card.dataset.hdFx) return;
    card.dataset.hdFx = '1';
    const gallery = card.querySelector('.card-gallery');
    if (!gallery) return;
    card.classList.add('hd-fx-card');
    if (!finePointer.matches) return;
    gallery.insertAdjacentHTML('beforeend',
      '<span class="hd-sheen" aria-hidden="true"></span><span class="hd-view-chip" aria-hidden="true">View piece</span>');
    if (still()) return;
    let raf = 0;
    card.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = gallery.getBoundingClientRect();
        gallery.style.setProperty('--hd-mx', `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
        gallery.style.setProperty('--hd-my', `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
      });
    });
  }
  function armCards() { $$('product-card').forEach(enhanceCard); }

  /* 6. Crisp images. Square product photos fill portrait frames with object-fit: cover, so
        the browser needs the frame HEIGHT worth of pixels, but Horizon's sizes only describe
        the width (and its lazy card images ask for 3840 px with sizes="auto"). Set each image's
        sizes to the CSS pixels it really covers; the browser multiplies by the screen density
        and picks from the existing srcset. Sizes only ever grow, so nothing re-downloads smaller. */
  const SIZE_SEL = 'main img[srcset], cart-drawer-component img[srcset], .hd-look__img';
  function fitSize(img) {
    if (!img.srcset || img.closest('.hd-hero__mark')) return;
    const r = img.getBoundingClientRect();
    if (r.width < 4 || r.height < 4) return;
    const nw = +img.getAttribute('width') || img.naturalWidth;
    const nh = +img.getAttribute('height') || img.naturalHeight;
    let need = r.width;
    if (nw && nh && getComputedStyle(img).objectFit === 'cover') need = Math.max(r.width, (r.height * nw) / nh);
    need = Math.ceil(need / 8) * 8;
    if (need <= (+img.dataset.hdSz || 0)) return;
    img.dataset.hdSz = String(need);
    img.sizes = `${need}px`;
  }
  let sizeRO = null;
  function armSizes(ctx = d) {
    if (!sizeRO && 'ResizeObserver' in window) {
      sizeRO = new ResizeObserver((entries) => { for (const e of entries) fitSize(e.target); });
    }
    $$(SIZE_SEL, ctx).forEach((img) => {
      if (img.dataset.hdSzOn) return;
      img.dataset.hdSzOn = '1';
      fitSize(img);
      if (sizeRO) sizeRO.observe(img);
    });
  }

  /* 7. Cart drawer scrim (desktop). Horizon's desktop drawer is a non-modal dialog, so it has
        no backdrop; the stylesheet stops the page squeeze and this adds a dim layer under the
        drawer that closes it on click. */
  function armDrawer() {
    const pw = d.querySelector('.page-wrapper');
    if (!pw) return;
    const scrim = d.createElement('div');
    scrim.className = 'hd-scrim';
    scrim.setAttribute('aria-hidden', 'true');
    d.body.appendChild(scrim);
    const wide = matchMedia('(min-width: 990px)');
    const sync = () => {
      const on = wide.matches && pw.classList.contains('page-wrapper--drawer-open') && !!d.querySelector('theme-drawer[open]');
      root.classList.toggle('hd-drawer-on', on);
    };
    new MutationObserver(sync).observe(pw, { attributes: true, attributeFilter: ['class'] });
    d.addEventListener('theme-drawer:open', () => requestAnimationFrame(sync));
    d.addEventListener('theme-drawer:close', () => requestAnimationFrame(sync));
    wide.addEventListener('change', sync);
    scrim.addEventListener('click', () => {
      $$('theme-drawer[open]').forEach((t) => { if (typeof t.close === 'function') t.close(); });
    });
    sync();
  }

  /* 8. Phone header logo hand-off: on the home page the hero wordmark is the logo; the header
        logo fades in once the hero mark has scrolled away (the stylesheet hides it until then). */
  function armMarkSwap() {
    const mark = d.querySelector('.hd-hero__mark');
    if (!mark) return;
    if (!('IntersectionObserver' in window)) { root.classList.add('hd-mark-gone'); return; }
    new IntersectionObserver((entries) => {
      for (const e of entries) root.classList.toggle('hd-mark-gone', !e.isIntersecting);
    }, { rootMargin: '-56px 0px 0px 0px' }).observe(mark);
  }

  /* 9. Keep "Wide-Leg" on one line in titles, and hint that wide tables scroll on phones. */
  function noBreak(ctx = d) {
    $$('product-card, .hd-look__cap, .product-details h1, .product-information h1, .resource-card__title', ctx).forEach((el) => {
      if (el.dataset.hdNb) return;
      el.dataset.hdNb = '1';
      const walker = d.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const hits = [];
      while (walker.nextNode()) if (/\w-\w/.test(walker.currentNode.nodeValue)) hits.push(walker.currentNode);
      for (const t of hits) {
        if (!t.parentElement || t.parentElement.closest('script, style, .hd-nb')) continue;
        const parts = t.nodeValue.split(/(\S*\w-\w\S*)/);
        if (parts.length < 2) continue;
        const frag = d.createDocumentFragment();
        parts.forEach((part, i) => {
          if (!part) return;
          if (i % 2) {
            const sp = d.createElement('span');
            sp.className = 'hd-nb';
            sp.textContent = part;
            frag.appendChild(sp);
          } else frag.appendChild(d.createTextNode(part));
        });
        t.replaceWith(frag);
      }
    });
  }
  function armTables() {
    $$('.rte table').forEach((t) => {
      const end = () => t.classList.toggle('hd-scrolled-end', t.scrollLeft + t.clientWidth >= t.scrollWidth - 4);
      t.addEventListener('scroll', end, { passive: true });
      end();
    });
  }

  /* 10. Free shipping gauge in the cart drawer, from the real cart total. */
  const FREE_CENTS = parseInt(cfg.freeShipCents || '7000', 10);
  const CURRENCY = cfg.currency || 'USD';
  let cart = null;
  const money = (cents) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: CURRENCY,
        minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (err) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };
  function paintShip() {
    if (!cart || !(FREE_CENTS > 0)) return;
    $$('cart-drawer-component .cart-drawer__inner').forEach((inner) => {
      let bar = inner.querySelector(':scope > .hd-ship');
      if (!cart.item_count) { if (bar) bar.remove(); return; }
      if (!bar) {
        bar = d.createElement('div');
        bar.className = 'hd-ship';
        bar.innerHTML = '<p class="hd-ship__msg" role="status" aria-live="polite"></p><div class="hd-ship__track" aria-hidden="true"><span class="hd-ship__fill"></span></div>';
        const head = inner.querySelector(':scope > .theme-drawer__header');
        if (head) head.after(bar); else inner.prepend(bar);
      }
      const total = cart.total_price;
      const left = Math.max(0, FREE_CENTS - total);
      const pct = `${(Math.min(1, total / FREE_CENTS) * 100).toFixed(1)}%`;
      if (bar.style.getPropertyValue('--hd-p') !== pct) bar.style.setProperty('--hd-p', pct);
      bar.classList.toggle('is-done', left === 0);
      const html = left === 0
        ? 'Free US shipping is on for this order.'
        : `You are <b>${money(left)}</b> away from free US shipping.`;
      const msg = bar.querySelector('.hd-ship__msg');
      if (msg.dataset.v !== html) { msg.dataset.v = html; msg.innerHTML = html; }
    });
  }
  let fetching = null;
  function refreshCart() {
    if (fetching) return fetching;
    const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetching = fetch(`${base}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) { cart = j; paintShip(); } })
      .catch(() => {})
      .finally(() => { fetching = null; });
    return fetching;
  }
  function armShip() {
    if (!d.querySelector('cart-drawer-component')) return;
    refreshCart();
    const later = () => setTimeout(refreshCart, 60);
    d.addEventListener('shopify:cart:lines-update', (e) => {
      if (e && e.promise && e.promise.then) e.promise.then(later, later); else later();
    });
    ['cart:update', 'cart:refresh'].forEach((n) => d.addEventListener(n, later));
    // Horizon re-renders the drawer contents after each change; put the gauge back if it went.
    const drawer = d.querySelector('cart-drawer-component');
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; paintShip(); });
    }).observe(drawer, { childList: true, subtree: true });
  }

  /* Boot. */
  const boot = () => {
    // Never come back to a drawer left open on another page (Horizon restores it on desktop;
    // over the scrim that reads as the page being stuck behind a sheet).
    try { sessionStorage.removeItem('theme-drawer-open'); } catch (err) { /* storage blocked */ }
    armSizes();
    markGlint();
    armCards();
    noBreak();
    armReveals();
    armParallax();
    armPause();
    armDrawer();
    armMarkSwap();
    armTables();
    armShip();
    // Horizon swaps product lists in place (filters, recommendations): enhance late cards too.
    if ('MutationObserver' in window) {
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          $$('product-card:not([data-hd-fx])').forEach(enhanceCard);
          armSizes();
          noBreak();
        });
      }).observe(d.body, { childList: true, subtree: true });
    }
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();
